import React, { useState } from 'react';
// Native MUI Components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Trip } from '@/types';
import * as tripService from '@/services/tripService';
import { useNavigate } from 'react-router-dom';
import { defaultAgeLevels, defaultTransportationOptions } from '@/utils/defaultValues';
import { useToast } from '@/hooks/use-toast';

interface AddTripFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AddTripForm: React.FC<AddTripFormProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    destination: '',
    startDate: '',
    endDate: '',
    defaultMembershipFee: 35,
    isSignUpOpen: false, // Standardmäßig geschlossen
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.destination || !formData.startDate || !formData.endDate) {
      return;
    }

    const newTripData: Omit<Trip, 'id'> = {
      ...formData,
      skiPasses: [],
      apartmentTypes: [],
      transportationOptions: defaultTransportationOptions,
      skiPassAgeLevels: defaultAgeLevels,
      statistics: {
        travellers: {
          total: 0,
          totalSkiClubMembers: 0,
        },
        apartments: {
          total: 0,
          totalNumberOfBeds: 0,
          totalOccupancy: 0,
        },
        skiPasses: {
          total: 0,
        },
      },
    };

    try {
      const tripId = await tripService.addTrip(newTripData);
      toast({ title: "Reise erstellt", description: `Die Reise "${formData.name}" wurde erfolgreich angelegt.` });
      onOpenChange(false);
      navigate(`/trip/${tripId}`);
    } catch (error) {
      console.error("Fehler beim Erstellen der Reise:", error);
      toast({ title: "Fehler", description: "Die Reise konnte nicht erstellt werden.", variant: "destructive" });
    }
  };

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => onOpenChange(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Typography variant="h6" component="div">
          Neue Reise erstellen
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => onOpenChange(false)}
          sx={{
            color: 'grey.500',
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Trip Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Alpine Adventure 2024"
                required
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Destination"
                value={formData.destination}
                onChange={(e) => handleChange('destination', e.target.value)}
                placeholder="e.g., Zermatt, Switzerland"
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleChange('startDate', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={(e) => handleChange('endDate', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Default Membership Fee (€)"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={formData.defaultMembershipFee}
                onChange={(e) => handleChange('defaultMembershipFee', parseFloat(e.target.value))}
                placeholder="35"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Fee charged to non-ski club members (can be customized per traveller)
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={() => onOpenChange(false)} 
          variant="outlined"
        >
          Abbrechen
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
        >
          Reise erstellen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTripForm;
